import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useGateData } from './hooks/useGateData';
import Header from './components/Header';
import TabBar from './components/TabBar';
import { Spinner } from './components/UI';
import Login from './pages/Login';
import Overview from './pages/Overview';
import Transactions from './pages/Transactions';
import AdminPanel from './pages/AdminPanel';
import LoadingDemand from './pages/LoadingDemand';

// Manual operator forms
import OperatorHome from './pages/OperatorHome';
import EntryForm from './pages/EntryForm';
import ExitForm from './pages/ExitForm';
import UpdateForm from './pages/UpdateForm';
import RejectVehicle from './pages/RejectVehicle';
import Reports from './pages/Reports';
import PhotoReview from './pages/PhotoReview';
import WeighbridgeForm from './pages/WeighbridgeForm';
import ChallanForm from './pages/ChallanForm';
import MaterialPrices from './pages/MaterialPrices';
import CharityForm from './pages/CharityForm';
import Charity from './pages/Charity';


// ── Protected route wrapper ────────────────────────────────────────────────────
function Protected({ children, requiredRole }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#F8F9FA',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Spinner size={32} color="#0D9488" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  // STRICT RULE: Operators are blocked from any dashboard page and bounced to forms
  if (requiredRole === 'DASHBOARD' && user.role === 'OPERATOR') {
    return <Navigate to="/forms" replace />;
  }
  // PHOTO_REVIEWER is a separate, narrow login — never sees the regular dashboard either
  if (requiredRole === 'DASHBOARD' && user.role === 'PHOTO_REVIEWER') {
    return <Navigate to="/photos" replace />;
  }
  // Same for WEIGHBRIDGE_OPERATOR, CHALAN_OPERATOR and PAYMENT_OPERATOR —
  // one screen each, nothing else
  if (requiredRole === 'DASHBOARD' && user.role === 'WEIGHBRIDGE_OPERATOR') {
    return <Navigate to="/weighbridge" replace />;
  }
  if (requiredRole === 'DASHBOARD' && user.role === 'PAYMENT_OPERATOR') {
    return <Navigate to="/payment" replace />;
  }
  if (requiredRole === 'DASHBOARD' && user.role === 'CHALAN_OPERATOR') {
    return <Navigate to="/challan" replace />;
  }

  if (requiredRole === 'OPERATOR' &&
      user.role !== 'OPERATOR' && user.role !== 'ADMIN') {
    if (user.role === 'PHOTO_REVIEWER') return <Navigate to="/photos" replace />;
    if (user.role === 'WEIGHBRIDGE_OPERATOR') return <Navigate to="/weighbridge" replace />;
    if (user.role === 'CHALAN_OPERATOR') return <Navigate to="/challan" replace />;
    if (user.role === 'PAYMENT_OPERATOR') return <Navigate to="/payment" replace />;
    return <Restricted msg="Operator or Admin role required." />;
  }

  if (requiredRole === 'PHOTO_REVIEWER' &&
      user.role !== 'PHOTO_REVIEWER' && user.role !== 'ADMIN') {
    if (user.role === 'OPERATOR') return <Navigate to="/forms" replace />;
    return <Restricted msg="Photo Reviewer or Admin role required." />;
  }

  if (requiredRole === 'WEIGHBRIDGE_OPERATOR' &&
      user.role !== 'WEIGHBRIDGE_OPERATOR' && user.role !== 'ADMIN') {
    return <Restricted msg="Weighbridge Operator or Admin role required." />;
  }

  if (requiredRole === 'PAYMENT_OPERATOR' &&
      user.role !== 'PAYMENT_OPERATOR' && user.role !== 'ADMIN') {
    return <Restricted msg="Payment Operator or Admin role required." />;
  }
  if (requiredRole === 'CHALAN_OPERATOR' &&
      user.role !== 'CHALAN_OPERATOR' && user.role !== 'ADMIN') {
    return <Restricted msg="Challan Operator or Admin role required." />;
  }

  if (requiredRole === 'ADMIN' && user.role !== 'ADMIN') {
    return <Restricted msg="Admin role required." />;
  }
  return children;
}

function Restricted({ msg }) {
  return (
    <div style={{
      padding: '48px 24px',
      textAlign: 'center',
      minHeight: '100vh',
      background: '#F8F9FA',
    }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
      <div style={{
        fontSize: 16,
        fontWeight: 700,
        color: '#1A1A1A',
        marginBottom: 6,
      }}>
        Access Restricted
      </div>
      <div style={{ fontSize: 13, color: '#5C6370' }}>{msg}</div>
    </div>
  );
}

// ── Stakeholder dashboard (Viewers & Admins only) ───────────────────────────────
function Dashboard() {
  const { user, loading: authLoading, isAdmin } = useAuth();
  const { transactions, loading, refreshing, lastRefresh, apiOnline, reload } = useGateData();
  const [tab, setTab] = useState('overview');

  if (authLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#F8F9FA',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Spinner size={32} color="#0D9488" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  // Double-check fallback security
  if (user.role === 'OPERATOR') return <Navigate to="/forms" replace />;
  if (user.role === 'PHOTO_REVIEWER') return <Navigate to="/photos" replace />;
  if (user.role === 'WEIGHBRIDGE_OPERATOR') return <Navigate to="/weighbridge" replace />;
  if (user.role === 'CHALAN_OPERATOR') return <Navigate to="/challan" replace />;
  if (user.role === 'PAYMENT_OPERATOR') return <Navigate to="/payment" replace />;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-light)',
      maxWidth: 520,
      margin: '0 auto',
      position: 'relative',
    }}>
      <Header apiOnline={apiOnline} lastRefresh={lastRefresh}
              refreshing={refreshing} onRefresh={reload} />
      <TabBar active={tab} onChange={setTab} isAdmin={isAdmin} />

      {loading ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 20px',
          gap: 16,
        }}>
          <Spinner size={32} color="#868E96" />
          <div style={{ fontSize: 13, color: '#5C6370' }}>Connecting to API...</div>
        </div>
      ) : (
        <>
          {tab === 'overview'     && <Overview transactions={transactions} />}
          {tab === 'loading'      && <LoadingDemand />}
          {tab === 'transactions' && <Transactions transactions={transactions} />}
          {tab === 'admin'        && (isAdmin
                                       ? <AdminPanel />
                                       : <Restricted msg={`Your role (${user.role}) cannot access Admin.`} />)}
          {tab === 'reports'      && <Reports />}
          {tab === 'charity'      && <Charity />}
        </>
      )}
    </div>
  );
}

// ── Root app with router ───────────────────────────────────────────────────────
export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />

          {/* Stakeholder dashboard — explicitly guarded against operators */}
          <Route path="/" element={
            <Protected requiredRole="DASHBOARD"><Dashboard /></Protected>
          } />

          {/* Operator station workspace */}
          <Route path="/forms" element={
            <Protected requiredRole="OPERATOR"><OperatorHome /></Protected>
          } />

          {/* Manual entry form */}
          <Route path="/entry" element={
            <Protected requiredRole="OPERATOR"><EntryForm /></Protected>
          } />

          {/* Manual exit form */}
          <Route path="/exit" element={
            <Protected requiredRole="OPERATOR"><ExitForm /></Protected>
          } />

          {/* Update form — correct a vehicle currently inside (audited) */}
          <Route path="/update" element={
            <Protected requiredRole="OPERATOR"><UpdateForm /></Protected>
          } />

          {/* Charity vehicle — a flow of its own. Same gate operators, but the
              vehicle is never weighed, billed or photographed, and the record
              lands in its own table well away from the sold trips. */}
          <Route path="/charity-vehicle" element={
            <Protected requiredRole="OPERATOR"><CharityForm /></Protected>
          } />

          {/* Reject a vehicle — admin only, reached from the header menu, not a tab */}
          <Route path="/reject-vehicle" element={
            <Protected requiredRole="ADMIN"><RejectVehicle /></Protected>
          } />

          {/* Photo review — separate narrow role, own page, no dashboard access */}
          <Route path="/photos" element={
            <Protected requiredRole="PHOTO_REVIEWER"><PhotoReview /></Protected>
          } />

          {/* Weighbridge — separate narrow role, own page, no dashboard access */}
          <Route path="/weighbridge" element={
            <Protected requiredRole="WEIGHBRIDGE_OPERATOR"><WeighbridgeForm /></Protected>
          } />

          {/* Challan — separate narrow role, own page, no dashboard access */}
          <Route path="/challan" element={
            <Protected requiredRole="CHALAN_OPERATOR"><ChallanForm mode="challan" /></Protected>
          } />

          {/* Payment desk — same station, other half of the billing pair.
              Separate role so neither counter can perform the other's step. */}
          <Route path="/payment" element={
            <Protected requiredRole="PAYMENT_OPERATOR"><ChallanForm mode="payment" /></Protected>
          } />

          {/* Material prices — admin only, reached from AdminPanel */}
          <Route path="/material-prices" element={
            <Protected requiredRole="ADMIN"><MaterialPrices /></Protected>
          } />

          {/* Catch-all fallback navigation */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}